import _ from 'underscore';
import React, { useEffect, useMemo } from 'react';
import { Activity } from 'botframework-directlinejs';
import { EVENT_NAME_ENGAGE_TROUBLESHOOTER, EVENT_NAME_LIVE_AGENT_CHAT, useAdsCopilotPilotMap } from '@bingads-webui/ads-copilot-ui';
import { type ActivityRenderer } from '@bingads-webui-react/msa-copilot-common';
import {
  LiveAgentSupportClosed,
  LiveAgentTransferCard,
  LiveAgentUnsupported,
} from '../index';
import {
  LIVE_AGENT_LIMITED_SUPPORT_LOCALES,
  isLiveChatSupportOpen,
  isLocaleSupported,
  LiveAgentTransferMessages,
  isEnglishOnlyChatSupportedLocale,
  shouldSkipLiveChatSupportOpenCheck,
} from '../../helpers';

export const LiveAgentTransferCardRenderer: ActivityRenderer = (props) => {
  const {
    activity,
    logger,
    onFeedbackButtonClick,
    params,
    liveAgentActivityProps: {
      isAIDegraded,
      locale,
      onLiveAgentTokensReceive,
      onLiveAgentTransferClick,
    },
    isInlineLiveAgentButtonEnabled,
  } = props;

  const {
    isIntelligentRoutingEnabled,
    isRedirectLiveAgentEnabled,
  } = useAdsCopilotPilotMap();

  const isEnglishOnlyChatSupportAvailable = useMemo(() => isEnglishOnlyChatSupportedLocale(locale), [locale]);
  const isLiveChatSupportOpenCheckSkipped = useMemo(() => shouldSkipLiveChatSupportOpenCheck(locale), [locale]);
  const liveAgentTransferType = useMemo(() => _.get(activity, 'valueType'), [activity]);

  const lowerLocale = locale ? locale.toLocaleLowerCase() : undefined;

  if (!lowerLocale || (!isLocaleSupported(lowerLocale) && !isEnglishOnlyChatSupportAvailable)) {
    logger?.trace(`Copilot - Live Agent: Unsupported card - Locale: ${lowerLocale}`);
    return <LiveAgentUnsupported onFeedbackButtonClick={onFeedbackButtonClick} />;
  }

  if (!isLiveChatSupportOpenCheckSkipped && !isLiveChatSupportOpen(lowerLocale) && !isEnglishOnlyChatSupportAvailable) {
    logger?.trace(`Copilot - Live Agent: Support closed card - Locale: ${lowerLocale}`);
    const { placeholderTime, placeholderTimezone, customPlaceholder } = LIVE_AGENT_LIMITED_SUPPORT_LOCALES[lowerLocale];
    return (
      <LiveAgentSupportClosed
        placeholderTime={placeholderTime}
        placeholderTimezone={placeholderTimezone}
        customPlaceholder={customPlaceholder}
        onFeedbackButtonClick={onFeedbackButtonClick}
      />
    );
  }

  if (activity?.channelData?.JwtTokens) {
    onLiveAgentTokensReceive(activity.channelData.JwtTokens);
  }

  // disable the card button if this activity is no longer the newest message
  const isCardButtonDisabled = useMemo<boolean>(() => {
    if (params.currentActivities) {
      for (let i = params.currentActivities.length - 1; i >= 0; i -= 1) {
        const a = params.currentActivities[i];
        if (a.id === activity.id) {
          // ConnectToAgent activity is the newest
          return false;
        }
        if (a.type === 'message') {
          return true;
        }
      }
    }
    return true;
  }, [activity.id, params.currentActivities]);

  const liveAgentCardText = useMemo(() => {
    if (liveAgentTransferType) {
      if (liveAgentTransferType in LiveAgentTransferMessages) {
        return LiveAgentTransferMessages[liveAgentTransferType];
      }
      logger?.trace(`Copilot - Live Agent: Transfer card - Unknown transfer type: ${liveAgentTransferType}`);
    }
    // Return a default message if the transfer type is unknown;
    return LiveAgentTransferMessages.ConnectToAgentNoAIResponse;
  }, [liveAgentTransferType]);

  useEffect(() => {
    if (isLiveChatSupportOpenCheckSkipped) {
      logger?.trace(`Copilot - Live Agent: Support hours check skipped. Locale: ${locale}`);
    }
    logger?.trace(`Copilot - Live Agent: Transfer card - Locale: ${locale}`);
  }, [locale, isLiveChatSupportOpenCheckSkipped]);

  if (isInlineLiveAgentButtonEnabled
    && (liveAgentTransferType === 'ConnectToAgentNoAIResponseRedScore'
      || liveAgentTransferType === 'ConnectToAgentWithAIResponseYellowScore')) {
    return null;
  }

  return (
    <LiveAgentTransferCard
      logger={logger}
      activity={activity}
      params={params}
      locale={locale}
      isEnglishOnlyChatSupportAvailable={isEnglishOnlyChatSupportAvailable}
      cardText={isRedirectLiveAgentEnabled ? liveAgentCardText : undefined}
      onLiveAgentTransferClick={onLiveAgentTransferClick}
      onFeedbackButtonClick={onFeedbackButtonClick}
      isCardButtonDisabled={isCardButtonDisabled}
      isIntelligentRoutingEnabled={isIntelligentRoutingEnabled}
      isAIDegraded={isAIDegraded}
    />
  );
};

LiveAgentTransferCardRenderer.canRender = (activity: Activity, isFromUser: boolean) => {
  const activityValue = _.get(activity, 'value');
  if (!isFromUser && activity.type === 'message' && activityValue === 'ConnectToAgent') {
    return true;
  }

  if (activity.type === 'event') {
    if (activity.name === EVENT_NAME_ENGAGE_TROUBLESHOOTER && activityValue === 'RedirectToLiveAgent') {
      return true;
    }
    if (activity.name === EVENT_NAME_LIVE_AGENT_CHAT) {
      return true;
    }
  }

  return false;
};
